import {
  LoadingOutlined,
  LoginOutlined,
  SettingOutlined,
  TeamOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  DashboardOutlined,
} from '@ant-design/icons';
import { Badge, Dropdown, Menu, Typography } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { isPlatformOrg } from 'domains/orgDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import NameBadge from 'features/Workspace/components/NameBadge';
import { usePage, useUser } from 'hooks';
import { useOrg } from 'hooks/useOrg';
import { useMeasurementUnit } from 'hooks/useMeasurementUnit';
import { MeasurementUnitEnum } from 'domains/measurementDomain';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  canSwitchToSourcer,
  canSwitchToSupplier,
  canVisitAdminSpace,
  hasLinkToQuickBooks,
  needLinkToQuickBooks,
} from 'utils/rbac';
import './Profile.less';
import api from 'utils/api';
import { QuickBooksCheckLoginResponse } from 'domains/workspaceDomain';
import { OrgTypeEnum } from 'features/Platform/components/OrgAdmin/OrgTypeSelectModal';
const Profile = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const user = useUser();
  const org = useOrg();
  const { unit: measurementUnit, switchUnit } = useMeasurementUnit();
  const { isPlatformPage } = usePage();
  const isLoadingOrg = useAppSelector(
    (state) => state.loading.effects.org.getOrgs
  );
  const orgs = useAppSelector((state) => state.org.list);

  const saasOrg =
    org?.orgType === OrgTypeEnum.SAAS || org?.orgType === OrgTypeEnum.CUSTOMER;

  const logout = async () => {
    dispatch.app.logout();
    window.location.href = '/login';
  };

  const fullName = useMemo(() => {
    return [user?.firstName, user?.lastName].filter((item) => item).join(' ');
  }, [user]);

  const switchUserHandle = async (param: SiteRoleEnum) => {
    await dispatch.account.switchUser(param);
    window.location.href = '/workspace';
  };

  const checkQuickBooksLogin = async (reqestId?: string) => {
    return api.post('/payments/quickbooks/checkLogin', { requestId: reqestId });
  };

  const linkToQuickBooks = async () => {
    const checkQuickBooksLoginResponse =
      (await checkQuickBooksLogin()) as QuickBooksCheckLoginResponse;
    if (!checkQuickBooksLoginResponse.hasLogin) {
      if (checkQuickBooksLoginResponse.loginUrl) {
        window.location.href = checkQuickBooksLoginResponse.loginUrl;
      }
    }
  };

  const menu = (
    <Menu>
      <Menu.Item className="profile-username">
        <RevxFlexLayout align="center">
          <NameBadge visible={false} name={user?.userFullName || ''} />
          <RevxFlexLayout
            direction="column"
            align="top"
            style={{
              marginLeft: '11px',
              marginRight: '8px',
            }}
          >
            <Typography.Title
              className="profile-username-ellipsis"
              style={{
                fontSize: '11px',
                marginBottom: 0,
                maxWidth: '142px',
              }}
              ellipsis={{
                tooltip: fullName,
              }}
            >
              {fullName}
            </Typography.Title>
            {/* <span>{user?.role?.name}</span> */}
          </RevxFlexLayout>
          {user?.isSsoUser && (
            <Badge
              status="success"
              text="SSO"
              size="small"
              style={{ fontSize: '8px' }}
            />
          )}
        </RevxFlexLayout>
      </Menu.Item>
      {!isPlatformPage && (
        <>
          {org &&
            !isPlatformOrg(org) &&
            (isEmpty(user?.manageOrgs) || (saasOrg && orgs?.length > 1) ? (
              <Menu.Item key="org">
                <span>{t('header.profile.currentOrg') + ' ' + org.name}</span>
              </Menu.Item>
            ) : (
              <Menu.SubMenu
                key="org"
                title={t('header.profile.currentOrg') + ' ' + org.name}
              >
                {orgs.map((manageOrg) => (
                  <Menu.Item
                    key={manageOrg.id}
                    onClick={() => dispatch.app.switchStoreOrg(manageOrg.id)}
                  >
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{manageOrg.name}</span>
                      {manageOrg.id === org?.id && (
                        <CheckCircleOutlined
                          style={{ color: '#52c41a', marginLeft: '8px' }}
                        />
                      )}
                    </span>
                  </Menu.Item>
                ))}
              </Menu.SubMenu>
            ))}
          {canVisitAdminSpace(user) && (
            <Menu.Item key="setting" icon={<SettingOutlined />} disabled={!org}>
              <a href="/platform/org" target="_blank">
                <span>
                  {t('header.profile.adminSpace')}{' '}
                  {isLoadingOrg && <LoadingOutlined />}
                </span>
              </a>
            </Menu.Item>
          )}
          {canSwitchToSourcer(user) && (
            <Menu.Item
              key="switchToSourcer"
              icon={<TeamOutlined />}
              disabled={!org}
              onClick={() => switchUserHandle(SiteRoleEnum.Sourcer)}
            >
              <span>
                {t('header.profile.switchToSourcer')}{' '}
                {isLoadingOrg && <LoadingOutlined />}
              </span>
            </Menu.Item>
          )}
          {canSwitchToSupplier(user) && (
            <Menu.Item
              key="SwitchToSupplier"
              icon={<TeamOutlined />}
              disabled={!org}
              onClick={() => switchUserHandle(SiteRoleEnum.Supplier)}
            >
              <span>
                {t('header.profile.SwitchToSupplier')}{' '}
                {isLoadingOrg && <LoadingOutlined />}
              </span>
            </Menu.Item>
          )}
          {needLinkToQuickBooks(user) && (
            <Menu.Item
              key="linkToQuickBooks"
              icon={<GlobalOutlined />}
              disabled={!org}
              onClick={() => linkToQuickBooks()}
            >
              <span>
                {t('header.profile.linkToQuickBooks')}{' '}
                {isLoadingOrg && <LoadingOutlined />}
              </span>
            </Menu.Item>
          )}
          {hasLinkToQuickBooks(user) && (
            <Menu.Item
              key="hasLinkedToQuickBooks"
              icon={<CheckCircleOutlined />}
              disabled={!org}
            >
              <span>
                {t('header.profile.hasLinkedToQuickBooks')}{' '}
                {isLoadingOrg && <LoadingOutlined />}
              </span>
            </Menu.Item>
          )}
        </>
      )}

      <Menu.SubMenu
        key="measurementUnit"
        icon={<DashboardOutlined />}
        title={t('header.profile.measurementUnit')}
      >
        {[MeasurementUnitEnum.IMPERIAL, MeasurementUnitEnum.METRIC].map(
          (option) => (
            <Menu.Item key={option} onClick={() => switchUnit(option)}>
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span>
                  {option === MeasurementUnitEnum.METRIC
                    ? t('common.measurementMetric')
                    : t('common.measurementImperial')}
                </span>
                {option === measurementUnit && (
                  <CheckCircleOutlined
                    style={{ color: '#52c41a', marginLeft: '8px' }}
                  />
                )}
              </span>
            </Menu.Item>
          )
        )}
      </Menu.SubMenu>

      <Menu.Item
        key="logout"
        onClick={logout}
        icon={<LoginOutlined />}
        className="logout-item"
      >
        <span>{t('header.profile.logout')}</span>
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="profile">
      {/*
        No getPopupContainer: the overlay belongs on document.body, which is where antd's
        z-index: 1050 actually applies. Rendering it inside the trigger trapped it in the sticky
        header's stacking context (z-index: 999), so any fixed element on the page - the Affix'd
        toolbar on the admin screens - painted over it.
      */}
      <Dropdown
        overlay={menu}
        overlayClassName="profile-overlay"
        placement="bottomRight"
      >
        <div className="profile-btn">
          <Badge text={'SSO'} size="small">
            <NameBadge visible={false} name={user?.userFullName || ''} />
          </Badge>
          {/* <Avatar size={24} icon={<UserOutlined />} />
          <span className="profile-username">
            {t('header.profile.username', { firstName: user?.firstName })}
          </span> */}
        </div>
      </Dropdown>
    </div>
  );
};

export default Profile;
